/**
 * Click to call price per minute, in minor units of the subscription currency. From the rate table in #6731.
 * Covers the Twilio legs, the recording, transcription and the AI summary.
 * Browser calls pay the destination price. Bridge calls (ring my phone) add the leg to the user's phone, priced by
 * the caller ID's country because the user's phone is assumed to be in the same country.
 */
export const CALL_PRICING = {
    currencies: [`USD`, `AUD`, `CAD`, `EUR`, `GBP`, `NZD`, `ZAR`],
    callerIdGroups: [
        { key: `AU_NZ`, label: `From an Australian or NZ number`, countries: [`AU`, `NZ`] },
        { key: `GB_US_CA`, label: `From a UK, US or Canadian number`, countries: [`GB`, `US`, `CA`] },
    ],
    /** type null means landline and mobile cost the same */
    destinations: [
        {
            label: `New Zealand landline`,
            countries: [`NZ`],
            type: `landline`,
            perMinuteCents: {
                AU_NZ: { USD: 5, AUD: 8, CAD: 8, EUR: 5, GBP: 4, NZD: 10, ZAR: 90 },
                GB_US_CA: { USD: 5, AUD: 8, CAD: 8, EUR: 5, GBP: 4, NZD: 10, ZAR: 90 },
            },
        },
        {
            label: `New Zealand mobile`,
            countries: [`NZ`],
            type: `mobile`,
            perMinuteCents: {
                AU_NZ: { USD: 11, AUD: 16, CAD: 16, EUR: 10, GBP: 8, NZD: 20, ZAR: 188 },
                GB_US_CA: { USD: 11, AUD: 16, CAD: 16, EUR: 10, GBP: 8, NZD: 20, ZAR: 188 },
            },
        },
        {
            label: `Australia landline`,
            countries: [`AU`],
            type: `landline`,
            perMinuteCents: {
                AU_NZ: { USD: 5, AUD: 7, CAD: 7, EUR: 4, GBP: 4, NZD: 9, ZAR: 80 },
                GB_US_CA: { USD: 5, AUD: 7, CAD: 7, EUR: 4, GBP: 4, NZD: 9, ZAR: 80 },
            },
        },
        {
            label: `Australia mobile`,
            countries: [`AU`],
            type: `mobile`,
            perMinuteCents: {
                AU_NZ: { USD: 10, AUD: 15, CAD: 14, EUR: 9, GBP: 8, NZD: 18, ZAR: 172 },
                GB_US_CA: { USD: 10, AUD: 15, CAD: 14, EUR: 9, GBP: 8, NZD: 18, ZAR: 172 },
            },
        },
        {
            label: `UK landline`,
            countries: [`GB`],
            type: `landline`,
            perMinuteCents: {
                AU_NZ: { USD: 4, AUD: 5, CAD: 5, EUR: 3, GBP: 3, NZD: 7, ZAR: 63 },
                GB_US_CA: { USD: 4, AUD: 6, CAD: 6, EUR: 4, GBP: 3, NZD: 7, ZAR: 69 },
            },
        },
        {
            label: `UK mobile`,
            countries: [`GB`],
            type: `mobile`,
            perMinuteCents: {
                AU_NZ: { USD: 5, AUD: 8, CAD: 8, EUR: 5, GBP: 4, NZD: 10, ZAR: 90 },
                GB_US_CA: { USD: 6, AUD: 8, CAD: 8, EUR: 5, GBP: 4, NZD: 10, ZAR: 98 },
            },
        },
        {
            label: `US or Canada`,
            countries: [`US`, `CA`],
            type: null,
            perMinuteCents: {
                AU_NZ: { USD: 4, AUD: 5, CAD: 5, EUR: 3, GBP: 3, NZD: 6, ZAR: 60 },
                GB_US_CA: { USD: 4, AUD: 6, CAD: 5, EUR: 3, GBP: 3, NZD: 7, ZAR: 65 },
            },
        },
    ],
    /** Bridge calls only, by caller ID country */
    bridgeLegCents: {
        NZ: { USD: 9, AUD: 12, CAD: 12, EUR: 8, GBP: 7, NZD: 15, ZAR: 141 },
        AU: { USD: 8, AUD: 11, CAD: 11, EUR: 7, GBP: 6, NZD: 14, ZAR: 126 },
        GB: { USD: 4, AUD: 5, CAD: 5, EUR: 3, GBP: 3, NZD: 6, ZAR: 51 },
        US: { USD: 2, AUD: 2, CAD: 2, EUR: 2, GBP: 2, NZD: 3, ZAR: 24 },
        CA: { USD: 2, AUD: 2, CAD: 2, EUR: 2, GBP: 2, NZD: 3, ZAR: 24 },
    },
};
/** A balance that covers fewer minutes than this gets a warning before the call */
export const CALL_LOW_BALANCE_MINUTES = 5;
/** Minutes a call may run past the balance before Twilio ends it. The overrun is charged, so the balance goes negative */
export const CALL_OVERDRAW_GRACE_MINUTES = 5;
/** Twilio's own cap on a dialled leg, 4 hours */
const TWILIO_MAX_TIME_LIMIT_SECONDS = 4 * 60 * 60;
/** Null when the caller ID, destination or currency is not in the table, so the call cannot be priced */
export function callPricePerMinuteCents(p) {
    const currency = p.currency.toUpperCase();
    const group = CALL_PRICING.callerIdGroups.find(item => { var _a; return item.countries.includes((_a = p.callerIdCountry) !== null && _a !== void 0 ? _a : ``); });
    const destination = CALL_PRICING.destinations.find(item => {
        var _a;
        return item.countries.includes((_a = p.destinationCountry) !== null && _a !== void 0 ? _a : ``) &&
            (item.type === null || item.type === p.destinationType);
    });
    if (!CALL_PRICING.currencies.includes(currency) || !group || !destination)
        return null;
    const base = destination.perMinuteCents[group.key][currency];
    if (!p.bridge)
        return base;
    const leg = CALL_PRICING.bridgeLegCents[p.callerIdCountry];
    return leg ? base + leg[currency] : null;
}
/** Dial timeLimit for a call: the whole minutes the balance pays for, plus the grace */
export function callMaxDurationSeconds(balanceCents, perMinuteCents) {
    const minutes = Math.max(0, Math.floor(balanceCents / perMinuteCents)) + CALL_OVERDRAW_GRACE_MINUTES;
    return Math.min(minutes * 60, TWILIO_MAX_TIME_LIMIT_SECONDS);
}
/** Twilio bills each started minute, so we do too */
export function callChargeCents(durationSeconds, perMinuteCents) {
    return Math.ceil(durationSeconds / 60) * perMinuteCents;
}
//# sourceMappingURL=calls.js.map